/**
 * Owner Links Reports Page Styles
 * 
 * Purpose:
 * Provides style definitions for the Owner Links & Reports page, including styles for the Reports
 * section (category cards, link cards, "Coming Soon" placeholders) and Help & Insights section
 * (grouped sections, help file cards, empty states). All styles use CSS custom properties for
 * theming and consistency with the owner portal design system.
 * 
 * High-level Workflow:
 * 1. Styles object exported as named export for use in OwnerLinksReportsPage.jsx
 * 2. Styles object passed as prop to HelpInsightsContent component
 * 3. Styles applied via inline style prop in JSX (React pattern)
 * 4. CSS custom properties resolve at runtime based on active theme
 * 
 * Assumptions & Invariants:
 * - All style values use CSS custom properties (var(--property-name)) for theming
 * - Style keys match exactly the keys used in JSX (styles.linksCard, styles.sectionTitle, etc.)
 * - Style values are verbatim from original inline styles object (no changes in Phase 3)
 * - Styles object structure unchanged (object with nested style definitions)
 * - CSS custom properties are defined in owner-ui.css or design-tokens.css
 * - Inline styles are applied via React style prop (not CSS classes)
 * - Future extension: Styles could be converted to CSS classes in a later phase
 * 
 * Dependencies:
 * - Internal:
 *   - CSS custom properties defined in owner-ui.css or design-tokens.css
 * - External:
 *   - None (pure JavaScript object)
 * 
 * Version: 1.0.0
 * 
 * Change Log:
 * - 2026-01-26: Phase 3 extraction - Extracted styles object from OwnerLinksReportsPage.jsx.
 *   Styles moved verbatim with no value or key changes. Exported as named export for import
 *   in OwnerLinksReportsPage.jsx and passed as prop to HelpInsightsContent.jsx.
 */

export const styles = {
  linksCard: {
    backgroundColor: 'var(--owner-card-bg)',
    border: 'var(--owner-card-border)',
    borderRadius: 'var(--owner-card-radius)',
    boxShadow: 'var(--owner-card-shadow)',
    padding: 'var(--spacing-6)',
    marginBottom: 'var(--spacing-4)',
    maxWidth: '1400px',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  sectionTitle: {
    fontSize: 'var(--font-size-xl)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    margin: 0,
    marginBottom: 'var(--spacing-4)',
  },
  linksGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 'var(--spacing-4)',
  },
  linkCard: {
    textAlign: 'center',
    padding: 'var(--spacing-6)',
    textDecoration: 'none',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 'var(--spacing-4)',
    backgroundColor: 'var(--owner-card-bg)',
    border: 'var(--owner-card-border)',
    borderRadius: 'var(--owner-card-radius)',
    boxShadow: 'var(--owner-card-shadow)',
    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)', // Match Calendar easing
    cursor: 'pointer',
    position: 'relative',
  },
  linkCardComingSoon: {
    textAlign: 'center',
    padding: 'var(--spacing-6)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 'var(--spacing-4)',
    backgroundColor: 'var(--owner-card-bg)',
    border: 'var(--owner-card-border)',
    borderRadius: 'var(--owner-card-radius)',
    boxShadow: 'var(--owner-card-shadow)',
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  comingSoonText: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    fontStyle: 'italic',
    marginTop: 'var(--spacing-1)',
  },
  errorMessage: {
    padding: 'var(--spacing-4)',
    color: 'var(--color-error)',
    fontSize: 'var(--font-size-base)',
  },
  linkIconContainer: {
    width: '56px',
    height: '56px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-brand-primary, var(--color-text-primary))',
    backgroundColor: 'var(--color-bg-subtle, rgba(0, 0, 0, 0.02))',
    borderRadius: 'var(--radius-md, 8px)',
    marginBottom: 'var(--spacing-1)',
  },
  linkTitle: {
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
  },
  reportsCard: {
    backgroundColor: 'var(--owner-card-bg)',
    border: 'var(--owner-card-border)',
    borderRadius: 'var(--owner-card-radius)',
    boxShadow: 'var(--owner-card-shadow)',
    padding: 'var(--spacing-6)',
    maxWidth: '1400px',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  maintenanceGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: 'var(--spacing-5)',
  },
  maintenancePanel: {
    border: '1px solid var(--color-border-light)',
    borderRadius: 'var(--radius-lg)',
    padding: 'var(--spacing-4)',
    backgroundColor: 'var(--owner-card-bg)',
  },
  maintenancePanelTitle: {
    fontSize: 'var(--font-size-lg)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    margin: '0 0 var(--spacing-3)',
  },
  maintenanceList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-3)',
  },
  maintenanceItem: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 'var(--spacing-3)',
    padding: 'var(--spacing-3)',
    border: '1px solid var(--color-border-light)',
    borderRadius: 'var(--radius-md)',
    backgroundColor: 'var(--color-bg-subtle, rgba(0, 0, 0, 0.02))',
  },
  maintenanceItemContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-1)',
    minWidth: 0,
  },
  maintenanceItemTitle: {
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    margin: 0,
  },
  maintenanceItemMeta: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    lineHeight: 1.5,
    margin: 0,
  },
  maintenanceActionButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px 14px',
    borderRadius: '999px',
    border: '1px solid var(--color-border-light)',
    backgroundColor: 'var(--owner-card-bg)',
    color: 'var(--color-text-primary)',
    cursor: 'pointer',
    fontWeight: 'var(--font-weight-medium)',
    whiteSpace: 'nowrap',
  },
  emptyState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'var(--spacing-8)',
    minHeight: '120px',
  },
  emptyStateText: {
    fontSize: 'var(--font-size-base)',
    color: 'var(--color-text-secondary)',
    fontStyle: 'italic',
  },
  // Help & Insights - Grouped sections with cards
  helpInsightsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-8)',
  },
  helpSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-4)',
  },
  helpSectionEmphasized: {
    backgroundColor: 'rgba(59, 130, 246, 0.03)', // Subtle blue tint
    border: '1px solid rgba(59, 130, 246, 0.15)',
    borderRadius: 'var(--radius-lg)',
    padding: 'var(--spacing-6)',
    margin: 'calc(-1 * var(--spacing-6))',
  },
  helpSectionHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-1)',
  },
  helpSectionTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-3)',
    flexWrap: 'wrap',
  },
  helpSectionTitle: {
    fontSize: 'var(--font-size-lg)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    margin: 0,
  },
  recommendedBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: 'var(--spacing-1) var(--spacing-2)',
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    color: 'rgb(59, 130, 246)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-medium)',
  },
  helpSectionDescription: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    margin: 0,
    lineHeight: 1.5,
  },
  helpCardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))',
    gap: 'var(--spacing-4)',
    // Responsive: single column on mobile (handled by minmax with 100% min)
  },
  helpCard: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: 'var(--spacing-4)',
    backgroundColor: 'var(--owner-card-bg)',
    border: '1px solid var(--color-border-light)',
    borderRadius: 'var(--radius-md)',
    textDecoration: 'none',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  helpCardContent: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-2)',
  },
  helpCardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 'var(--spacing-2)',
  },
  helpCardIcon: {
    flexShrink: 0,
    color: 'var(--color-text-secondary)',
    marginTop: '2px',
  },
  helpCardTitle: {
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    lineHeight: 1.4,
  },
  helpCardDescription: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    lineHeight: 1.5,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  helpCardAction: {
    flexShrink: 0,
    marginLeft: 'var(--spacing-3)',
    color: 'var(--color-text-secondary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.6,
    transition: 'opacity 0.2s',
  },
};
